import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormAutoComplete, FormNumberInput } from '@/core/ui';
import {
  getLlmModelOptionsForProvider,
  isLlmModelValidForProvider,
  supportsReasoningEffort,
} from './llm-model-capabilities';
import { PromptIdSelect } from './prompt-id-select';
import type { AiConfigBaseName, AiConfigPhaseKey, IStationSchema } from './schema';

const REASONING_EFFORT_OPTIONS = [
  { key: 'none', label: 'None' },
  { key: 'minimal', label: 'Minimal' },
  { key: 'low', label: 'Low' },
  { key: 'medium', label: 'Medium' },
  { key: 'high', label: 'High' },
  { key: 'xhigh', label: 'Extra high' },
];

const REASONING_SUMMARY_OPTIONS = [
  { key: 'auto', label: 'Auto' },
  { key: 'concise', label: 'Concise' },
  { key: 'detailed', label: 'Detailed' },
];

const LLM_PROVIDER_OPTIONS = [
  { key: 'openai', label: 'OpenAI' },
  { key: 'groq', label: 'Groq' },
  { key: 'google', label: 'Google' },
  { key: 'xai', label: 'XAI' },
  { key: 'moonshotai', label: 'Moonshot' },
  { key: 'deepseek', label: 'DeepSeek' },
];
const TRANSCRIPT_MODE_OPTIONS = [
  { key: 'full', label: 'Full', description: 'Both user and AI transcript' },
  { key: 'user_only', label: 'User only', description: 'Only the user side of the conversation' },
];

export const AiConfigLlmTab = ({
  baseName,
  phaseKey,
}: {
  baseName: AiConfigBaseName;
  phaseKey: AiConfigPhaseKey;
}) => {
  const { control, resetField } = useFormContext<IStationSchema>();
  const llm = useWatch({ control, name: `${baseName}.llm` });
  const provider = llm?.provider;
  const model = llm?.model;
  const shouldShowReasoningEffort = supportsReasoningEffort(model);
  const showExtendedPromptFields = phaseKey === 'markscheme' || phaseKey === 'result';

  const modelOptions = useMemo(() => {
    const options = getLlmModelOptionsForProvider(provider);
    const currentModel = model?.trim();

    if (!currentModel) return options;

    const isInCatalog = options.some((option) => option.key === currentModel);
    if (isInCatalog) return options;

    return [...options, { key: currentModel, label: currentModel }];
  }, [provider, model]);

  return (
    <div className='grid grid-cols-6 gap-4 pt-3'>
      <FormAutoComplete<IStationSchema>
        control={control}
        name={`${baseName}.llm.provider`}
        label='Provider'
        placeholder='Select provider'
        options={LLM_PROVIDER_OPTIONS}
        onValueChange={(newProvider) => {
          const providerKey = newProvider != null && newProvider !== '' ? String(newProvider) : '';
          if (!model || !providerKey) return;

          if (!isLlmModelValidForProvider(model, providerKey)) {
            resetField(`${baseName}.llm.model`, { defaultValue: '' });
          }
        }}
        variant='faded'
        className='rounded col-span-3'
      />
      <FormAutoComplete<IStationSchema>
        control={control}
        name={`${baseName}.llm.model`}
        label='Model'
        placeholder={provider ? 'Select model' : 'Select provider first'}
        options={modelOptions}
        isDisabled={!provider}
        variant='faded'
        className='rounded col-span-3'
      />

      {!shouldShowReasoningEffort ? (
        <FormNumberInput<IStationSchema>
          control={control}
          name={`${baseName}.llm.temperature`}
          label='Temperature'
          placeholder='0.2'
          variant='faded'
          className='rounded col-span-3'
        />
      ) : (
        <>
          <FormAutoComplete<IStationSchema>
            control={control}
            name={`${baseName}.llm.reasoning_summary`}
            label='Reasoning summary'
            placeholder='Select reasoning summary'
            options={REASONING_SUMMARY_OPTIONS}
            variant='faded'
            className='rounded col-span-3'
          />
          <FormAutoComplete<IStationSchema>
            control={control}
            name={`${baseName}.llm.reasoning_effort`}
            label='Reasoning effort'
            placeholder='Select reasoning effort'
            options={REASONING_EFFORT_OPTIONS}
            variant='faded'
            className='rounded col-span-3'
          />
        </>
      )}
      <FormNumberInput<IStationSchema>
        control={control}
        name={`${baseName}.llm.max_completion_tokens`}
        label='Tokens'
        placeholder='1024'
        variant='faded'
        className='rounded col-span-3'
      />

      {showExtendedPromptFields ? (
        <>
          <PromptIdSelect
            baseName={baseName}
            phaseKey={phaseKey}
            field='promptId'
            label='System prompt'
            className='rounded col-span-3'
          />
          <PromptIdSelect
            baseName={baseName}
            phaseKey={phaseKey}
            field='userPromptId'
            label='User prompt'
            className='rounded col-span-3'
          />
          <FormAutoComplete<IStationSchema>
            control={control}
            name={`${baseName}.llm.transcriptMode`}
            label='Transcript mode'
            placeholder='Select transcript mode'
            options={TRANSCRIPT_MODE_OPTIONS}
            variant='faded'
            className='rounded col-span-3'
          />
        </>
      ) : (
        <PromptIdSelect baseName={baseName} phaseKey={phaseKey} />
      )}
    </div>
  );
};
